'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';

/**
 * Props for `StatHighlights`.
 */
export type StatHighlightsProps =
  SliceComponentProps<Content.StatHighlightsSlice>;

/**
 * Component for "StatHighlights" Slices.
 *
 * Features:
 * - Title and description at the top
 * - CTA button
 * - Vertical list of stat cards with value and label
 */
const StatHighlights: FC<StatHighlightsProps> = ({ slice }) => {
  const { title, description, cta_button, stat_list } = slice.primary;

  const headerRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: headerRef, delay: '0ms' },
      { ref: statsRef, delay: '200ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative bg-[#0E1010] py-16 md:py-20 lg:py-28 px-4 md:px-8"
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-6 items-center">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="flex flex-col gap-6 items-center text-center w-full"
        >
          {/* Title */}
          {isFilled.richText(title) && (
            <div className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white">
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => <h2>{children}</h2>,
                }}
              />
            </div>
          )}

          {/* Description */}
          {isFilled.richText(description) && (
            <div className="text-base md:text-lg font-normal leading-[1.5] text-white">
              <PrismicRichText field={description} />
            </div>
          )}

          {/* CTA Button */}
          {isFilled.link(cta_button) && (
            <PrismicNextLink
              field={cta_button}
              className="bg-[rgba(234,234,234,0.3)] border-t border-[#eaeaea] rounded-full px-6 py-3 font-bold text-white text-base text-center hover:bg-white/40 transition-colors"
            >
              {cta_button.text || 'Get Started'}
            </PrismicNextLink>
          )}
        </div>

        {/* Stats List */}
        {stat_list && stat_list.length > 0 && (
          <div
            ref={statsRef}
            className="flex flex-col gap-4 w-full items-center"
          >
            {stat_list.map((stat, index) => (
              <div
                key={index}
                className="bg-[rgba(10,39,34,0.8)] rounded-3xl px-6 py-4 flex items-center justify-center gap-2"
              >
                {/* Stat Value */}
                {stat.stat_value && (
                  <span className="text-[32px] md:text-[40px] font-bold leading-[1.2] text-white">
                    {stat.stat_value}
                  </span>
                )}

                {/* Stat Label */}
                {stat.stat_label && (
                  <span className="text-sm md:text-base font-medium leading-[1.5] text-white">
                    {stat.stat_label}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default StatHighlights;
